import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite, hasPermission } from '../../rbac';
import { contentAPI, projectAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import './Projects.css';
import './AdminStaffMemberForm.css';

const CATEGORIES = ['water_supply', 'road', 'drainage', 'sanitation', 'education', 'health', 'agriculture', 'housing', 'electricity', 'community', 'other'];
const BLANK_DOCUMENT = { title: '', url: '', meta: '' };

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'road',
  estimated_cost: '',
  actual_cost: '',
  funding_source: '',
  location: '',
  work_code: '',
  financial_year: '',
  beneficiary: '',
  contractor_name: '',
  contractor_contact: '',
  project_length: '',
  start_date: '',
  expected_end_date: '',
  status: 'proposed',
  progress_percentage: 0,
  documents: [],
  gallery: [],
  updatesText: '',
};

const BLANK_PHOTO = { url: '', caption: '' };

const normalizeGallery = items => {
  if (Array.isArray(items)) {
    return items.map(item => ({
      url: String(item?.url || item?.image_url || '').trim(),
      caption: String(item?.caption || item?.title || '').trim(),
    })).filter(item => item.url);
  }
  if (typeof items === 'string') {
    try {
      const parsed = JSON.parse(items);
      if (Array.isArray(parsed)) return normalizeGallery(parsed);
    } catch {
      return parsePipeLines(items, ['url', 'caption']);
    }
  }
  return [];
};

const parsePipeLines = (value, keys) => (value || '')
  .split('\n')
  .map(line => line.trim())
  .filter(Boolean)
  .map(line => {
    const parts = line.split('|').map(part => part.trim());
    return keys.reduce((acc, key, index) => {
      if (parts[index]) acc[key] = parts[index];
      return acc;
    }, {});
  })
  .filter(item => Object.keys(item).length);

const stringifyPipeLines = (items, keys) => Array.isArray(items)
  ? items.map(item => keys.map(key => item?.[key] || '').filter(Boolean).join('|')).join('\n')
  : '';

const normalizeDocuments = items => Array.isArray(items)
  ? items.map(item => ({
      title: String(item?.title || item?.name || '').trim(),
      url: String(item?.url || item?.file_url || '').trim(),
      meta: String(item?.meta || item?.description || '').trim(),
    })).filter(item => item.title || item.url)
  : [];

const fileSizeLabel = size => {
  const bytes = Number(size || 0);
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const documentMetaFromUpload = file => {
  const ext = file?.name?.split('.').pop()?.toUpperCase();
  return [ext, fileSizeLabel(file?.size)].filter(Boolean).join(' - ');
};

const numberOrNull = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const apiErrorMessage = (err, fallback) => {
  const details = err.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return details.map(item => item.message || item.field).filter(Boolean).join(', ');
  }
  return err.response?.data?.error || err.message || fallback;
};

export default function Projects() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'projects', user);
  const canCreateThis = hasPermission(user, 'projects', 'create');
  const canUpdateThis = hasPermission(user, 'projects', 'update');
  const canDeleteThis = hasPermission(user, 'projects', 'delete');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploadingDocumentIndex, setUploadingDocumentIndex] = useState(null);
  const [uploadingPhotoIndex, setUploadingPhotoIndex] = useState(null);
  const [sort, setSort] = useState({ key: 'title', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [currentStep, setCurrentStep] = useState(0);

  const projectSteps = [
    { title: 'मूलभूत माहिती', subtitle: 'Basic details' },
    { title: 'अर्थसंकल्प व अंमलबजावणी', subtitle: 'Budget & execution' },
    { title: 'कालावधी व प्रगती', subtitle: 'Timeline & progress' },
    { title: 'दस्तऐवज', subtitle: 'Documents' },
    { title: 'छायाचित्रे', subtitle: 'Photos & Gallery' },
    { title: 'पुनरावलोकन', subtitle: 'Updates & review' },
  ];

  useBackButtonClose(showForm, () => {
    setShowForm(false);
    setEditItem(null);
    setCurrentStep(0);
  });

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await projectAPI.getAll(slug);
      setProjects(res.data.projects || []);
    } catch (err) {
      setError('लोड करण्यात अयशस्वी');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const buildPayload = () => ({
    title: formData.title.trim(),
    description: formData.description.trim() || null,
    category: formData.category,
    estimated_cost: numberOrNull(formData.estimated_cost),
    actual_cost: numberOrNull(formData.actual_cost),
    funding_source: formData.funding_source.trim() || null,
    location: formData.location.trim() || null,
    work_code: formData.work_code.trim() || null,
    financial_year: formData.financial_year.trim() || null,
    beneficiary: formData.beneficiary.trim() || null,
    contractor_name: formData.contractor_name.trim() || null,
    contractor_contact: formData.contractor_contact.trim() || null,
    project_length: formData.project_length.trim() || null,
    start_date: formData.start_date || null,
    expected_end_date: formData.expected_end_date || null,
    status: formData.status,
    progress_percentage: numberOrNull(formData.progress_percentage) ?? 0,
    documents: normalizeDocuments(formData.documents),
    gallery: normalizeGallery(formData.gallery),
    progress_updates: parsePipeLines(formData.updatesText, ['title', 'date', 'description']),
  });

  const handleSubmit = async event => {
    event.preventDefault();
    if (event.nativeEvent?.submitter?.dataset?.projectSubmit !== 'true') return;
    setError('');
    setSuccess('');
    if (!formData.title.trim()) {
      setError('Project title is required.');
      return;
    }
    if (formData.start_date && formData.expected_end_date && formData.expected_end_date < formData.start_date) {
      setError('Expected end date cannot be before start date.');
      return;
    }
    try {
      if (editItem) {
        await projectAPI.update(slug, editItem.id, buildPayload());
        setSuccess('प्रकल्प अद्ययावत केला!');
      } else {
        await projectAPI.create(slug, buildPayload());
        setSuccess('प्रकल्प तयार केला!');
      }
      setShowForm(false);
      setEditItem(null);
      setFormData({ ...EMPTY_FORM, documents: [] });
      setCurrentStep(0);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'अयशस्वी'));
    }
  };

  const handleEdit = item => {
    setEditItem(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'road',
      estimated_cost: item.estimated_cost || '',
      actual_cost: item.actual_cost || '',
      funding_source: item.funding_source || '',
      location: item.location || '',
      work_code: item.work_code || '',
      financial_year: item.financial_year || '',
      beneficiary: item.beneficiary || '',
      contractor_name: item.contractor_name || '',
      contractor_contact: item.contractor_contact || '',
      project_length: item.project_length || '',
      start_date: item.start_date?.split('T')[0] || '',
      expected_end_date: item.expected_end_date?.split('T')[0] || '',
      status: item.status || 'proposed',
      progress_percentage: item.progress_percentage || 0,
      documents: normalizeDocuments(item.documents),
      gallery: normalizeGallery(item.gallery),
      updatesText: stringifyPipeLines(item.progress_updates, ['title', 'date', 'description']),
    });
    setShowForm(true);
    setCurrentStep(0);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditItem(null);
    setFormData({ ...EMPTY_FORM, documents: [], gallery: [] });
    setCurrentStep(0);
    setError('');
  };

  const moveProjectStep = nextStep => {
    setError('');
    if (nextStep > currentStep) {
      if (currentStep === 0 && !formData.title.trim()) {
        setError('Project title is required before continuing.');
        return;
      }
      if (currentStep === 2 && formData.start_date && formData.expected_end_date && formData.expected_end_date < formData.start_date) {
        setError('Expected end date cannot be before start date.');
        return;
      }
    }
    setCurrentStep(Math.max(0, Math.min(nextStep, projectSteps.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateDocument = (index, field, value) => {
    setFormData(prev => {
      const documents = [...prev.documents];
      documents[index] = { ...documents[index], [field]: value };
      return { ...prev, documents };
    });
  };

  const addDocument = () => {
    setFormData(prev => ({ ...prev, documents: [...prev.documents, { ...BLANK_DOCUMENT }] }));
  };

  const removeDocument = index => {
    setFormData(prev => ({ ...prev, documents: prev.documents.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const updatePhoto = (index, field, value) => {
    setFormData(prev => {
      const gallery = [...(prev.gallery || [])];
      gallery[index] = { ...gallery[index], [field]: value };
      return { ...prev, gallery };
    });
  };

  const addPhoto = () => {
    setFormData(prev => ({ ...prev, gallery: [...(prev.gallery || []), { ...BLANK_PHOTO }] }));
  };

  const removePhoto = index => {
    setFormData(prev => ({ ...prev, gallery: (prev.gallery || []).filter((_, itemIndex) => itemIndex !== index) }));
  };

  const uploadPhotoFile = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file || !slug) return;
    event.target.value = '';
    setUploadingPhotoIndex(index);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await contentAPI.uploadFile(slug, data);
      setFormData(prev => {
        const gallery = [...(prev.gallery || [])];
        const current = gallery[index] || { ...BLANK_PHOTO };
        const fallbackCaption = file.name.replace(/\.[^.]+$/, '');
        gallery[index] = {
          ...current,
          caption: current.caption || fallbackCaption,
          url: res.data?.url || current.url,
        };
        return { ...prev, gallery };
      });
    } catch (err) {
      setError(apiErrorMessage(err, 'छायाचित्र अपलोड करण्यात अयशस्वी'));
    } finally {
      setUploadingPhotoIndex(null);
    }
  };

  const importPhotosFromDocuments = () => {
    setFormData(prev => {
      const currentGallery = [...(prev.gallery || [])];
      const existingUrls = new Set(currentGallery.map(p => p.url));
      const imageDocs = (prev.documents || []).filter(doc => {
        const isImg = /\.(png|jpe?g|webp|gif|svg)/i.test(doc.url) || /PNG|JPG|JPEG|WEBP|IMAGE/i.test(doc.meta);
        return isImg && doc.url && !existingUrls.has(doc.url);
      });
      if (!imageDocs.length) return prev;
      const imported = imageDocs.map(doc => ({
        url: doc.url,
        caption: doc.title || 'कामाचे छायाचित्र',
      }));
      return { ...prev, gallery: [...currentGallery, ...imported] };
    });
  };

  const uploadDocumentFile = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file || !slug) return;
    event.target.value = '';
    setUploadingDocumentIndex(index);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await contentAPI.uploadFile(slug, data);
      setFormData(prev => {
        const documents = [...prev.documents];
        const current = documents[index] || { ...BLANK_DOCUMENT };
        const fallbackTitle = file.name.replace(/\.[^.]+$/, '');
        documents[index] = {
          ...current,
          title: current.title || fallbackTitle,
          url: res.data?.url || current.url,
          meta: current.meta || documentMetaFromUpload(file),
        };
        return { ...prev, documents };
      });
    } catch (err) {
      setError(apiErrorMessage(err, 'दस्तऐवज अपलोड करण्यात अयशस्वी'));
    } finally {
      setUploadingDocumentIndex(null);
    }
  };

  const handleDelete = async item => {
    if (!window.confirm(`"${item.title}" प्रकल्प हटवायचा आहे का?`)) return;
    setError('');
    setSuccess('');
    try {
      await projectAPI.delete(slug, item.id);
      setSuccess('प्रकल्प हटवला');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'प्रकल्प हटवण्यात अयशस्वी');
    }
  };

  const filteredProjects = useMemo(() => projects.filter(project =>
    (!search || [project.title,project.location,project.work_code,project.contractor_name].filter(Boolean).some(value => String(value).toLowerCase().includes(search.toLowerCase()))) &&
    (!categoryFilter || project.category === categoryFilter) &&
    (!statusFilter || project.status === statusFilter)
  ).sort((a,b) => {
    const numeric = new Set(['estimated_cost','progress_percentage']);
    const left = numeric.has(sort.key) ? Number(a[sort.key] || 0) : String(a[sort.key] || '').toLowerCase();
    const right = numeric.has(sort.key) ? Number(b[sort.key] || 0) : String(b[sort.key] || '').toLowerCase();
    return (left > right ? 1 : left < right ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
  }), [projects, search, categoryFilter, statusFilter, sort]);
  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
  const visibleProjects = filteredProjects.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page,totalPages]);
  const changeSort = key => { setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' })); setPage(1); };
  const SortHeader = ({column,children}) => <button className={`project-sort-header ${sort.key === column ? 'active' : ''}`} onClick={() => changeSort(column)}>{children}<span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span></button>;
  const exportCsv = () => { const cell=value=>`"${String(value??'').replace(/"/g,'""')}"`; const rows=[['Project','Category','Estimated cost','Actual cost','Progress','Status','Location','Financial year'],...filteredProjects.map(item=>[item.title,item.category,item.estimated_cost,item.actual_cost,item.progress_percentage,item.status,item.location,item.financial_year])]; const link=document.createElement('a');link.href=URL.createObjectURL(new Blob(['\ufeff',rows.map(row=>row.map(cell).join(',')).join('\n')],{type:'text/csv;charset=utf-8'}));link.download=`projects-${new Date().toISOString().slice(0,10)}.csv`;link.click();URL.revokeObjectURL(link.href); };

  return (
    <div>
      <div className="admin-card-header">
        <h2>विकास प्रकल्प</h2>
        {(showForm ? canWriteThis : canCreateThis) && (
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditItem(null);
              setFormData({ ...EMPTY_FORM, documents: [] });
              setCurrentStep(0);
            }}
          >
            {showForm ? 'रद्द करा' : '+ प्रकल्प जोडा'}
          </button>
        )}
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="admin-card staff-member-form project-wizard-form">
          <h1>{editItem ? 'विकास प्रकल्प संपादित करा' : 'नवीन विकास प्रकल्प'}</h1>
          <form onSubmit={handleSubmit}>
            <nav className="staff-form-stepper" aria-label="Project form steps">
              {projectSteps.map((step, index) => (
                <button type="button" key={step.title}
                  className={`${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  disabled={index > currentStep} onClick={() => moveProjectStep(index)}
                  aria-current={index === currentStep ? 'step' : undefined}>
                  <span>{index < currentStep ? '✓' : index + 1}</span>
                  <div><strong>{step.title}</strong><small>{step.subtitle}</small></div>
                </button>
              ))}
            </nav>
            <div className="form-inner">
            {currentStep === 0 && <section className="form-section staff-step-panel">
              <h3>मूलभूत प्रकल्प माहिती (Basic Information)</h3>
              <div className="form-group"><label>शीर्षक *</label><input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required /></div>
              <div className="form-group"><label>प्रवर्ग</label><select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>{CATEGORIES.map(category => <option key={category} value={category}>{category.replace(/_/g, ' ').toUpperCase()}</option>)}</select></div>
              <div className="form-group"><label>स्थळ</label><input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} /></div>
              <div className="form-group"><label>काम कोड</label><input value={formData.work_code} onChange={e => setFormData({ ...formData, work_code: e.target.value })} /></div>
              <div className="form-group"><label>आर्थिक वर्ष</label><input value={formData.financial_year} onChange={e => setFormData({ ...formData, financial_year: e.target.value })} placeholder="2026-27" /></div>
              <div className="form-group"><label>लांबी / क्षमता</label><input value={formData.project_length} onChange={e => setFormData({ ...formData, project_length: e.target.value })} placeholder="उदा. 2.5 कि.मी." /></div>
              <div className="form-group"><label>लाभार्थी / उपयोगकर्ते</label><input value={formData.beneficiary} onChange={e => setFormData({ ...formData, beneficiary: e.target.value })} /></div>
              <div className="form-group project-full-field"><label>वर्णन</label><textarea rows="4" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} /></div>
            </section>}

            {currentStep === 1 && <section className="form-section staff-step-panel">
              <h3>अर्थसंकल्प व अंमलबजावणी (Budget & Execution)</h3>
              <div className="form-group"><label>अंदाजित खर्च</label><input type="number" min="0" value={formData.estimated_cost} onChange={e => setFormData({ ...formData, estimated_cost: e.target.value })} /></div>
              <div className="form-group"><label>प्रत्यक्ष खर्च</label><input type="number" min="0" value={formData.actual_cost} onChange={e => setFormData({ ...formData, actual_cost: e.target.value })} /></div>
              <div className="form-group project-full-field"><label>निधी स्रोत</label><input value={formData.funding_source} onChange={e => setFormData({ ...formData, funding_source: e.target.value })} /></div>
              <div className="form-group"><label>कंत्राटदार / एजन्सी</label><input value={formData.contractor_name} onChange={e => setFormData({ ...formData, contractor_name: e.target.value })} /></div>
              <div className="form-group"><label>संपर्क क्रमांक</label><input inputMode="numeric" value={formData.contractor_contact} onChange={e => setFormData({ ...formData, contractor_contact: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="10 अंकी मोबाइल क्रमांक" /></div>
            </section>}

            {currentStep === 2 && <section className="form-section staff-step-panel">
              <h3>कालावधी, स्थिती व प्रगती (Timeline & Progress)</h3>
              <div className="form-group"><label>सुरुवात तारीख</label><input type="date" max={formData.expected_end_date || undefined} value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} /></div>
              <div className="form-group"><label>अपेक्षित शेवट तारीख</label><input type="date" min={formData.start_date || undefined} value={formData.expected_end_date} onChange={e => setFormData({ ...formData, expected_end_date: e.target.value })} /></div>
              <div className="form-group"><label>स्थिती</label><select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}><option value="proposed">प्रस्तावित</option><option value="approved">मंजूर</option><option value="in_progress">प्रगतीपथावर</option><option value="completed">पूर्ण</option><option value="cancelled">रद्द</option></select></div>
              <div className="form-group project-progress-field"><label>प्रगती: <strong>{formData.progress_percentage}%</strong></label><input type="range" min="0" max="100" value={formData.progress_percentage} onChange={e => setFormData({ ...formData, progress_percentage: e.target.value })} /></div>
            </section>}

            {currentStep === 3 && <section className="form-section staff-step-panel project-documents-step">
                <div className="project-step-heading">
                  <div><h3>प्रकल्प दस्तऐवज (Project Documents)</h3><p>मंजुरी पत्र, अंदाजपत्रक, कार्यादेश किंवा इतर सार्वजनिक दस्तऐवज जोडा.</p></div>
                  <button type="button" className="btn btn-outline btn-sm" onClick={addDocument}>+ दस्तऐवज जोडा</button>
                </div>
                {formData.documents.length === 0 ? (
                  <div className="project-empty-documents">
                    <strong>अद्याप दस्तऐवज जोडलेले नाहीत</strong><span>दस्तऐवज ऐच्छिक आहेत आणि नंतरही जोडता येतील.</span>
                  </div>
                ) : (
                  <div className="project-document-list">
                    {formData.documents.map((document, index) => (
                      <article className="project-document-editor" key={index}>
                          <div className="form-group"><label>शीर्षक</label><input value={document.title} onChange={e => updateDocument(index, 'title', e.target.value)} placeholder="उदा. प्रशासकीय मंजुरी पत्र" /></div>
                          <div className="form-group"><label>फाईल URL</label><input value={document.url} onChange={e => updateDocument(index, 'url', e.target.value)} placeholder="अपलोड केल्यानंतर URL येथे येईल" /></div>
                          <div className="form-group"><label>माहिती</label><input value={document.meta} onChange={e => updateDocument(index, 'meta', e.target.value)} placeholder="PDF - 245 KB" /></div>
                          <button type="button" className="project-document-remove" onClick={() => removeDocument(index)}>हटवा</button>
                        <div className="project-document-upload">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                            onChange={event => uploadDocumentFile(index, event)}
                            disabled={uploadingDocumentIndex === index}
                          />
                          {uploadingDocumentIndex === index && <span>अपलोड होत आहे...</span>}
                          {document.url && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                              {/\.(png|jpe?g|webp|gif)/i.test(document.url) && (
                                <img src={document.url} alt="प्रिव्ह्यू" style={{ width: 36, height: 28, objectFit: 'cover', borderRadius: 4, border: '1px solid #cbd5e1' }} />
                              )}
                              <a href={document.url} target="_blank" rel="noreferrer">फाईल उघडा ↗</a>
                            </div>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
            </section>}

            {currentStep === 4 && <section className="form-section staff-step-panel project-photos-step">
                <div className="project-step-heading">
                  <div>
                    <h3>प्रकल्प छायाचित्रे (Project Photos & Gallery)</h3>
                    <p>कामाची सद्यस्थिती, पायाभरणी, प्रत्यक्ष काम व पूर्णत्वाची छायाचित्रे जोडा.</p>
                  </div>
                  <div className="project-step-heading-actions">
                    {formData.documents.some(d => /\.(png|jpe?g|webp|gif)/i.test(d.url)) && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={importPhotosFromDocuments}>
                        + दस्तऐवजांमधून चित्रे आणा
                      </button>
                    )}
                    <button type="button" className="btn btn-outline btn-sm" onClick={addPhoto}>
                      + छायाचित्र जोडा
                    </button>
                  </div>
                </div>

                {(!formData.gallery || formData.gallery.length === 0) ? (
                  <div className="project-empty-documents">
                    <strong>अद्याप छायाचित्रे जोडलेली नाहीत</strong>
                    <span>विकास कामाचे फोटो जोडल्यास नागरिक पोर्टलवर ते थेट छायाचित्रे विभागात दिसतील.</span>
                    <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 10 }} onClick={addPhoto}>
                      + पहिले छायाचित्र जोडा
                    </button>
                  </div>
                ) : (
                  <div className="project-photo-list">
                    {formData.gallery.map((photo, index) => (
                      <article className="project-photo-editor" key={index}>
                        <div className="project-photo-thumb-wrap">
                          {photo.url ? (
                            <img src={photo.url} alt={photo.caption || 'कामाचे छायाचित्र'} className="project-photo-thumb-img" />
                          ) : (
                            <div className="project-photo-thumb-placeholder">फोटो निवडा किंवा URL टाका</div>
                          )}
                        </div>

                        <div className="project-photo-fields">
                          <div className="form-group">
                            <label>कॅप्शन (शीर्षक / माहिती)</label>
                            <input
                              value={photo.caption}
                              onChange={e => updatePhoto(index, 'caption', e.target.value)}
                              placeholder="उदा. रस्त्याचे काँक्रीटीकरण करताना छायाचित्र"
                            />
                          </div>

                          <div className="form-group">
                            <label>फोटो URL</label>
                            <input
                              value={photo.url}
                              onChange={e => updatePhoto(index, 'url', e.target.value)}
                              placeholder="अपलोड केल्यानंतर URL येथे येईल किंवा बाह्य URL टाका"
                            />
                          </div>

                          <div className="project-photo-upload-row">
                            <input
                              type="file"
                              accept="image/*,.jpg,.jpeg,.png,.webp"
                              onChange={event => uploadPhotoFile(index, event)}
                              disabled={uploadingPhotoIndex === index}
                            />
                            {uploadingPhotoIndex === index && <span>अपलोड होत आहे...</span>}
                            {photo.url && (
                              <a href={photo.url} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto' }}>
                                फोटो उघडा ↗
                              </a>
                            )}
                          </div>
                        </div>

                        <button type="button" className="project-photo-remove" onClick={() => removePhoto(index)}>
                          हटवा
                        </button>
                      </article>
                    ))}
                  </div>
                )}
            </section>}

            {currentStep === 5 && <section className="form-section staff-step-panel">
              <h3>सार्वजनिक अद्यतने व पुनरावलोकन (Updates & Review)</h3>
              <div className="form-group project-full-field">
                <label>प्रगती अद्यतने — प्रत्येक ओळ: शीर्षक | YYYY-MM-DD | वर्णन</label>
                <textarea rows="4" value={formData.updatesText} onChange={e => setFormData({ ...formData, updatesText: e.target.value })} placeholder="काम सुरू झाले|2026-05-20|प्राथमिक काम सुरू झाले." />
              </div>

              {formData.gallery && formData.gallery.length > 0 && (
                <div className="form-group project-full-field">
                  <label>जोडलेली छायाचित्रे ({formData.gallery.length})</label>
                  <div className="project-review-photos-grid">
                    {formData.gallery.map((photo, i) => photo.url ? (
                      <div key={i} className="project-review-photo-item" title={photo.caption}>
                        <img src={photo.url} alt={photo.caption || 'छायाचित्र'} />
                      </div>
                    ) : null)}
                  </div>
                </div>
              )}

              <div className="staff-review-grid">
                <div><span>प्रकल्प</span><strong>{formData.title || '—'}</strong></div>
                <div><span>प्रवर्ग</span><strong>{formData.category.replace(/_/g, ' ')}</strong></div>
                <div><span>अंदाजित खर्च</span><strong>₹{Number(formData.estimated_cost || 0).toLocaleString('en-IN')}</strong></div>
                <div><span>कालावधी</span><strong>{formData.start_date || 'निश्चित नाही'} — {formData.expected_end_date || 'निश्चित नाही'}</strong></div>
                <div><span>स्थिती</span><strong>{formData.status.replace(/_/g, ' ')}</strong></div>
                <div><span>प्रगती</span><strong>{formData.progress_percentage}%</strong></div>
                <div><span>दस्तऐवज</span><strong>{formData.documents.length} फाईल्स</strong></div>
                <div><span>छायाचित्रे</span><strong>{(formData.gallery || []).length} फोटो</strong></div>
              </div>
            </section>}
            </div>
            <div className="form-actions staff-step-actions">
              <button type="button" className="btn-secondary" onClick={closeForm}>रद्द करा</button>
              <div>
                {currentStep > 0 && <button type="button" className="btn-secondary" onClick={() => moveProjectStep(currentStep - 1)}>← मागील</button>}
                {currentStep < projectSteps.length - 1 ? (
                  <button key="project-next" type="button" className="btn-primary" onClick={() => moveProjectStep(currentStep + 1)}>पुढील →</button>
                ) : (
                  <button key="project-submit" type="submit" data-project-submit="true" className="btn-primary">{editItem ? 'प्रकल्प अद्ययावत करा' : 'प्रकल्प तयार करा'}</button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="admin-card project-table-card">
            <div className="project-table-toolbar">
              <label className="project-search"><span>⌕</span><input placeholder="शीर्षक, ठिकाण किंवा काम क्रमांक शोधा..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} /></label>
              <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व प्रवर्ग</option>
                {CATEGORIES.map(category => <option key={category} value={category}>{category.replace(/_/g, ' ').toUpperCase()}</option>)}
              </select>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व स्थिती</option>
                <option value="proposed">प्रस्तावित</option>
                <option value="approved">मंजूर</option>
                <option value="in_progress">प्रगतीपथावर</option>
                <option value="completed">पूर्ण</option>
                <option value="cancelled">रद्द</option>
              </select>
              <button className="project-export-btn" onClick={exportCsv}>⇩ CSV निर्यात</button>
            </div>
            <div className="project-table-meta"><span><strong>{filteredProjects.length}</strong> प्रकल्प नोंदी</span><span className={`project-load-status ${loading ? 'visible' : ''}`}><i/> माहिती अद्ययावत होत आहे</span></div>
            <div className={`table-scroll project-table-scroll ${loading ? 'is-refreshing' : ''}`}>
              <table className="data-table project-data-table">
                <thead><tr><th><SortHeader column="title">नाव</SortHeader></th><th><SortHeader column="category">प्रवर्ग</SortHeader></th><th><SortHeader column="estimated_cost">अंदाजित खर्च</SortHeader></th><th><SortHeader column="progress_percentage">प्रगती</SortHeader></th><th><SortHeader column="status">स्थिती</SortHeader></th><th>कृती</th></tr></thead>
                <tbody>
                  {!loading && visibleProjects.length === 0 && <tr><td colSpan="6" className="project-empty">प्रकल्प सापडले नाहीत.</td></tr>}
                  {visibleProjects.map(project => (
                    <tr key={project.id}>
                      <td><div className="project-title-cell"><strong>{project.title}</strong><small>{project.work_code || project.location || 'ठिकाण उपलब्ध नाही'}</small></div></td>
                      <td><span className="project-category">{project.category?.replace(/_/g, ' ')}</span></td>
                      <td className="project-cost">₹{Number(project.estimated_cost || 0).toLocaleString('en-IN')}</td>
                      <td>
                        <div className="project-progress"><div><i style={{ width: `${project.progress_percentage || 0}%` }}/></div><span>{project.progress_percentage || 0}%</span>
                        </div>
                      </td>
                      <td><span className={`project-status status-${project.status}`}>{project.status?.replace(/_/g,' ')}</span></td>
                      <td>
                        {(canUpdateThis || canDeleteThis) && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {canUpdateThis && <button className="project-edit-btn" onClick={() => handleEdit(project)}>संपादन</button>}
                            {canDeleteThis && <button className="project-delete-btn" onClick={() => handleDelete(project)}>हटवा</button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-pagination project-pagination"><label>प्रति पृष्ठ <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}><option>10</option><option>20</option><option>50</option></select></label><button className="btn btn-outline btn-sm" disabled={page===1} onClick={()=>setPage(value=>Math.max(1,value-1))}>← मागील</button><span>पृष्ठ <strong>{page}</strong> / {totalPages}</span><button className="btn btn-outline btn-sm" disabled={page>=totalPages} onClick={()=>setPage(value=>Math.min(totalPages,value+1))}>पुढील →</button></div>
        </div>
      )}
    </div>
  );
}
